import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, UtensilsCrossed } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-8">
      <div className="w-full max-w-sm" data-testid="reset-password-page">
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center bg-brand"><UtensilsCrossed className="h-5 w-5 text-white" /></div>
          <span className="font-heading text-lg font-bold tracking-tight">CateringDiary</span>
        </div>
        {!token ? (
          <p data-testid="reset-invalid" className="border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">This reset link is missing its token — request a fresh one from the sign-in page.</p>
        ) : done ? (
          <div className="text-center">
            <CheckCircle2 className="mx-auto h-10 w-10 text-brand" />
            <h1 className="mt-3 font-heading text-xl font-bold" data-testid="reset-success">Password updated</h1>
            <p className="mt-1 text-sm text-muted-foreground">You can sign in with your new password now.</p>
            <Link to="/login" data-testid="reset-login-link" className="mt-4 inline-block text-sm font-semibold text-brand hover:text-brand-dark">Go to sign in →</Link>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-2xl font-bold tracking-tight">Set a new password</h1>
            <form onSubmit={submit} className="mt-6 space-y-5" data-testid="reset-form">
              <div className="space-y-2"><Label htmlFor="new-password">New password</Label><Input data-testid="reset-password-input" id="new-password" type="password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} required /></div>
              <div className="space-y-2"><Label htmlFor="confirm-password">Confirm password</Label><Input data-testid="reset-confirm-input" id="confirm-password" type="password" minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} required /></div>
              {error && <p data-testid="reset-error" className="border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
              <Button data-testid="reset-submit-button" type="submit" disabled={loading} className="w-full rounded-md bg-ink hover:bg-ink-light">{loading ? "Updating…" : "Update password"}</Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
